import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import AddIcon from "@material-ui/icons/Add";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import InputAdornment from "@material-ui/core/InputAdornment";
import Chip from "@material-ui/core/Chip";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import FormHelperText from "@material-ui/core/FormHelperText";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  urlField: {
    "& .MuiInputBase-root": {
      fontSize: "0.875rem",
    },
  },
  statusChip: {
    fontWeight: "bold",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_WEBHOOKS") {
    const webhooks = action.payload;
    return [...webhooks];
  }
  if (action.type === "UPDATE_WEBHOOK") {
    const webhook = action.payload;
    const webhookIndex = state.findIndex((w) => w.id === webhook.id);
    if (webhookIndex !== -1) {
      state[webhookIndex] = webhook;
      return [...state];
    } else {
      return [webhook, ...state];
    }
  }
  if (action.type === "DELETE_WEBHOOK") {
    const webhookId = action.payload;
    return state.filter((w) => w.id !== webhookId);
  }
  if (action.type === "RESET") {
    return [];
  }
};

const Webhooks = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  
  const [loading, setLoading] = useState(false);
  const [webhooks, dispatch] = useReducer(reducer, []);
  const [webhookModalOpen, setWebhookModalOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [webhookName, setWebhookName] = useState("");
  const [flowId, setFlowId] = useState("");
  const [whatsappId, setWhatsappId] = useState("");
  const [whatsapps, setWhatsapps] = useState([]);

  useEffect(() => {
    if (user && user.profile === "admin") {
      loadWebhooks();
      loadWhatsapps();
    }
  }, [user]);

  const loadWebhooks = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/webhooks");
      dispatch({ type: "LOAD_WEBHOOKS", payload: data });
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const loadWhatsapps = async () => {
    try {
      const { data } = await api.get("/whatsapp");
      setWhatsapps(data);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWebhookModal = () => {
    setSelectedWebhook(null);
    setWebhookName("");
    setFlowId("");
    setWhatsappId("");
    setWebhookModalOpen(true);
  };

  const handleCloseWebhookModal = () => {
    setWebhookModalOpen(false);
    setSelectedWebhook(null);
    setWebhookName("");
    setFlowId("");
    setWhatsappId("");
  };

  const handleSaveWebhook = async () => {
    if (!webhookName.trim()) {
      toast.error("Digite um nome para o webhook");
      return;
    }

    setLoading(true);
    try {
      const payload = { name: webhookName };
      
      if (flowId && flowId.trim() !== "") {
        payload.flowId = parseInt(flowId);
      }
      
      if (whatsappId && whatsappId !== "") {
        payload.whatsappId = parseInt(whatsappId);
      }

      if (selectedWebhook) {
        const { data } = await api.put("/webhooks/" + selectedWebhook.id, payload);
        dispatch({ type: "UPDATE_WEBHOOK", payload: data });
        toast.success("Webhook atualizado com sucesso!");
      } else {
        const { data } = await api.post("/webhooks", payload);
        dispatch({ type: "UPDATE_WEBHOOK", payload: data });
        toast.success("Webhook criado com sucesso!");
      }
      handleCloseWebhookModal();
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleEditWebhook = (webhook) => {
    setSelectedWebhook(webhook);
    setWebhookName(webhook.name);
    
    if (webhook.config && webhook.config.details && webhook.config.details.idFlow) {
      setFlowId(webhook.config.details.idFlow.toString());
    } else {
      setFlowId("");
    }
    
    if (webhook.config && webhook.config.details && webhook.config.details.whatsappId) {
      setWhatsappId(webhook.config.details.whatsappId.toString());
    } else {
      setWhatsappId("");
    }
    
    setWebhookModalOpen(true);
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm("Tem certeza que deseja deletar este webhook?")) {
      return;
    }

    setLoading(true);
    try {
      await api.delete("/webhooks/" + webhookId);
      dispatch({ type: "DELETE_WEBHOOK", payload: webhookId });
      toast.success("Webhook deletado com sucesso!");
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleToggleWebhook = async (webhook) => {
    setLoading(true);
    try {
      const { data } = await api.patch("/webhooks/" + webhook.id + "/toggle");
      dispatch({ type: "UPDATE_WEBHOOK", payload: data });
      toast.success("Webhook " + (data.active ? "ativado" : "desativado") + " com sucesso!");
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("URL copiada para a área de transferência!");
  };

  const getFlowName = (webhook) => {
    if (webhook.config && webhook.config.details && webhook.config.details.idFlow) {
      return "Flow #" + webhook.config.details.idFlow;
    }
    return "Nenhum";
  };

  const getWhatsappName = (webhook) => {
    if (webhook.config && webhook.config.details && webhook.config.details.whatsappId) {
      const wpp = whatsapps.find(w => w.id === webhook.config.details.whatsappId);
      return wpp ? wpp.name : "WhatsApp #" + webhook.config.details.whatsappId;
    }
    return "Primeiro disponível";
  };

  if (!user || user.profile !== "admin") {
    return null;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Gerenciamento de Webhooks</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenWebhookModal}
            startIcon={<AddIcon />}
          >
            Adicionar Webhook
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">ID</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>URL do Webhook</TableCell>
              <TableCell>Flow ID</TableCell>
              <TableCell>WhatsApp</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Requisições</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {webhooks.map((webhook) => (
              <TableRow key={webhook.id}>
                <TableCell align="center">{webhook.id}</TableCell>
                <TableCell>{webhook.name}</TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={webhook.webhookUrl}
                    className={classes.urlField}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Copiar URL">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyUrl(webhook.webhookUrl)}
                            >
                              <FileCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </TableCell>
                <TableCell>{getFlowName(webhook)}</TableCell>
                <TableCell>{getWhatsappName(webhook)}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={webhook.active ? "Ativo" : "Inativo"}
                    color={webhook.active ? "primary" : "default"}
                    size="small"
                    className={classes.statusChip}
                  />
                </TableCell>
                <TableCell align="center">{webhook.requestAll || 0}</TableCell>
                <TableCell align="center">
                  <Tooltip title={webhook.active ? "Desativar" : "Ativar"}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleWebhook(webhook)}
                    >
                      <PowerSettingsNewIcon
                        style={{ color: webhook.active ? "#4caf50" : "#999" }}
                      />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton
                      size="small"
                      onClick={() => handleEditWebhook(webhook)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Deletar">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteWebhook(webhook.id)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {webhooks.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="textSecondary">
                    Nenhum webhook cadastrado. Clique em "Adicionar Webhook" para começar.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={webhookModalOpen} onClose={handleCloseWebhookModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedWebhook ? "Editar Webhook" : "Novo Webhook"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Nome do Webhook"
            fullWidth
            variant="outlined"
            value={webhookName}
            onChange={(e) => setWebhookName(e.target.value)}
            autoFocus
            margin="dense"
            placeholder="Ex: B4You - Carrinho Abandonado"
          />
          
          <TextField
            label="Flow ID (Opcional)"
            fullWidth
            variant="outlined"
            value={flowId}
            onChange={(e) => setFlowId(e.target.value)}
            margin="normal"
            placeholder="Ex: 3"
            helperText="Digite o ID do FlowBuilder que deseja vincular (você pode ver o ID na URL ao editar um flow)"
            type="number"
          />

          <FormControl fullWidth variant="outlined" margin="normal">
            <InputLabel id="whatsapp-select-label">Conexão WhatsApp</InputLabel>
            <Select
              labelId="whatsapp-select-label"
              id="whatsapp-select"
              value={whatsappId}
              onChange={(e) => setWhatsappId(e.target.value)}
              label="Conexão WhatsApp"
            >
              <MenuItem value="">
                <em>Primeiro disponível</em>
              </MenuItem>
              {whatsapps.map((whatsapp) => (
                <MenuItem key={whatsapp.id} value={whatsapp.id}>
                  {whatsapp.name} ({whatsapp.status === "CONNECTED" ? "Conectado" : whatsapp.status})
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Escolha qual conexão WhatsApp enviará as mensagens deste webhook</FormHelperText>
          </FormControl>

          <Typography variant="caption" color="textSecondary" style={{ marginTop: 16, display: "block" }}>
            Após criar, você receberá uma URL única para configurar na plataforma externa (B4You, etc).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWebhookModal}>Cancelar</Button>
          <Button onClick={handleSaveWebhook} color="primary" variant="contained">
            {selectedWebhook ? "Salvar" : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Webhooks;
